import emailjs from "@emailjs/browser";
import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  Logo,
  partnerWithUsSvg,
  sponsorAChildSvg,
  volunteerWithUsSvg,
} from "../assets";

const Contact = () => {
  const [goal, setGoal] = useState("None");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  // Scroll to section if hash exists
  useEffect(() => {
    if (window.location.hash === "#join-us-section") {
      setTimeout(() => {
        const element = document.getElementById("join-us-section");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  const handleOnSubmit = (e) => {
    e.preventDefault();
    
    if (!goal || goal === "None") {
      toast.error("Please select what you're looking for");
      return;
    }

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_CONNECT_TEMPLATE_ID,
        {
          senders_name: name,
          senders_phone: phone,
          senders_location: location,
          senders_email: email,
          senders_message: message,
          senders_goal: goal,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        (result) => {
          setName("");
          setPhone("");
          setLocation("");
          setEmail("");
          setMessage("");
          setGoal("None");

          toast.success("Form submitted successfully!");
        },
        (error) => {
          toast.error("Something went wrong!! Please try again");
        }
      );
  };

  return (
    <div className="w-full min-h-screen bg-[#FAFAFA]">
      <Toaster position="top-center" reverseOrder={false} />

      {/* Header Section */}
      <div className='w-full px-4 sm:px-[50px] py-[40px] sm:py-[60px] flex flex-col items-center justify-center gap-[12px] bg-[#EE645A] font-poppins text-center text-white'>
        <h1 className='font-bold text-[28px] sm:text-[40px]'>
          Connect . Help . Do Good
        </h1>

        <p className='max-w-[900px] h-auto font-normal text-[14px] sm:text-[18px] text-[#FFFFFFCC] mt-3'>
          Join the Movement, Ignite Change! Get in touch today and become a part of our mission to create a better world.
        </p>
      </div>

      {/* Main Content */}
      <div className='max-w-[1200px] mx-auto px-4 py-12'>
        
        {/* Contact Form Section */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12'>
          
          {/* Left Side - Info */}
          <div className='flex flex-col items-center justify-center space-y-8'>
            <div>
              <img
                src={Logo}
                alt="Bhavishya Logo"
                className="w-32 h-44 mx-auto"
              />
            </div>
            <div className='font-poppins text-[16px] text-[#333333] leading-relaxed text-center'>
              <p className='mb-4'>
                Join the Movement, Ignite Change! Get in touch today and become a part of our mission to create a better world.
              </p>
              <p className='text-[#EE645A] font-bold'>
                1 out of every 2 street children have suffered verbal, physical or sexual abuse. The time is now to make a difference!
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className='bg-white rounded-[16px] shadow-lg p-8'>
            <form className='space-y-6' onSubmit={handleOnSubmit}>
              
              {/* Goal Selection */}
              <div className='space-y-3'>
                <label className='block font-bold text-[16px] text-[#000000]'>
                  What are you looking for?
                </label>
                <div className='space-y-2'>
                  <div className='flex items-center gap-3'>
                    <input
                      type="checkbox"
                      id="volunteer"
                      className="w-5 h-5 rounded cursor-pointer accent-[#EE645A]"
                      checked={goal === "Volunteer"}
                      onChange={() => setGoal("Volunteer")}
                    />
                    <label htmlFor="volunteer" className='cursor-pointer text-[15px] text-[#333333] font-medium'>
                      Volunteer with us
                    </label>
                  </div>
                  <div className='flex items-center gap-3'>
                    <input
                      type="checkbox"
                      id="pledge"
                      className="w-5 h-5 rounded cursor-pointer accent-[#EE645A]"
                      checked={goal === "Pledge"}
                      onChange={() => setGoal("Pledge")}
                    />
                    <label htmlFor="pledge" className='cursor-pointer text-[15px] text-[#333333] font-medium'>
                      Sponsor a Child
                    </label>
                  </div>
                  <div className='flex items-center gap-3'>
                    <input
                      type="checkbox"
                      id="partner"
                      className="w-5 h-5 rounded cursor-pointer accent-[#EE645A]"
                      checked={goal === "Partner"}
                      onChange={() => setGoal("Partner")}
                    />
                    <label htmlFor="partner" className='cursor-pointer text-[15px] text-[#333333] font-medium'>
                      Partner with us
                    </label>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className='block text-[14px] font-medium text-[#666666] mb-2'>Full Name *</label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className='w-full px-4 py-2.5 border-2 border-[#EEE] rounded-[8px] font-poppins text-[14px] focus:outline-none focus:border-[#EE645A] transition-colors'
                />
              </div>

              {/* Phone and Location */}
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-[14px] font-medium text-[#666666] mb-2'>Phone Number *</label>
                  <input
                    type="tel"
                    placeholder="Your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className='w-full px-4 py-2.5 border-2 border-[#EEE] rounded-[8px] font-poppins text-[14px] focus:outline-none focus:border-[#EE645A] transition-colors'
                  />
                </div>
                <div>
                  <label className='block text-[14px] font-medium text-[#666666] mb-2'>Location *</label>
                  <input
                    type="text"
                    placeholder="Your city/location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                    className='w-full px-4 py-2.5 border-2 border-[#EEE] rounded-[8px] font-poppins text-[14px] focus:outline-none focus:border-[#EE645A] transition-colors'
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className='block text-[14px] font-medium text-[#666666] mb-2'>Email Address *</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className='w-full px-4 py-2.5 border-2 border-[#EEE] rounded-[8px] font-poppins text-[14px] focus:outline-none focus:border-[#EE645A] transition-colors'
                />
              </div>

              {/* Message */}
              <div>
                <label className='block text-[14px] font-medium text-[#666666] mb-2'>Message *</label>
                <textarea
                  placeholder="Tell us more about your interest..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows="4"
                  className='w-full px-4 py-2.5 border-2 border-[#EEE] rounded-[8px] font-poppins text-[14px] focus:outline-none focus:border-[#EE645A] transition-colors resize-none'
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className='w-full py-3 bg-[#EE645A] text-white font-bold text-[16px] rounded-[8px] hover:bg-[#D84530] transition-colors duration-300 shadow-md hover:shadow-lg'
              >
                Send Message
              </button>
            </form>
          </div>
        </div>

        {/* Join Us Section - New Card Layout */}
        <div id="join-us-section" className='w-full min-h-[452px] px-4 sm:px-6 lg:px-[60px] py-[32px] bg-[#FEF7ED] rounded-[16px] mb-12 scroll-mt-20'>
          <div className='max-w-[1200px] mx-auto text-center'>
            <h2 className='text-[20px] sm:text-[24px] font-medium font-poppins'>
              Join Us
            </h2>
            <p className="mt-2 text-[14px] sm:text-[16px] font-inter text-[#666666]">
              Be part of meaningful change. Whether you want to volunteer in our community drives or gain valuable experience through our internship program, there's a place for you.
            </p>
          </div>

          <div className='w-full flex flex-col lg:flex-row gap-[24px] justify-center mt-8'>
            
            {/* Join Our Drive Card */}
            <div className='flex flex-col items-center w-full max-w-[524px] bg-[#EB5A57] rounded-2xl mx-auto'>
              <h1 className='mt-6 font-medium text-[20px] sm:text-[24px] text-center text-white'>
                Join Our Drive
              </h1>
              
              <p className="m-4 text-[14px] sm:text-[16px] text-[#FFFFFF]/75 text-center leading-relaxed">
                Volunteer with us in impactful community initiatives. From tree plantation drives to food distribution, health camps to educational programs — your time and effort can create lasting change in the lives of those who need it most.
              </p>
              
              <a 
                href="https://wa.me/7808056151?text=Hi%20Bhavishya,%20I%20would%20like%20to%20volunteer%20with%20your%20organization.%20Can%20you%20tell%20me%20about%20upcoming%20drives%3F" 
                target="_blank" 
                rel="noopener noreferrer"
                className='w-full flex justify-center'
              >
                <button className='mt-auto mb-6 text-white w-[90%] sm:w-[500px] rounded-[8px] h-[40px] bg-black hover:bg-[#333333] transition-colors duration-300'>
                  Join Our Drives
                </button>
              </a>
            </div>

            
            {/* Apply for Internship Card */}
            <div className='flex flex-col items-center w-full max-w-[524px] bg-[#EB5A57] rounded-2xl mx-auto'>
              <h1 className='mt-6 font-medium text-[20px] sm:text-[24px] text-center text-white'>
                Apply for Internship
              </h1>
              
              <p className="m-4 text-[14px] sm:text-[16px] text-[#FFFFFF]/75 text-center leading-relaxed">
                Gain hands-on experience in the nonprofit sector while making a real difference. Work alongside our team on education programs, community outreach, digital marketing, and more. Build your skills while building a better tomorrow.
              </p>
              
              <a 
                href="https://docs.google.com/forms/d/e/1FAIpQLSehJrdvcReDfnvLsIUXeXk0wBsU_V7qfgykqsKORkN-IVJ7dA/viewform" 
                target="_blank" 
                rel="noopener noreferrer"
                className='w-full flex justify-center'
              >
                <button className='mt-auto mb-6 text-white w-[90%] sm:w-[500px] rounded-[8px] h-[40px] bg-black hover:bg-[#333333] transition-colors duration-300'>
                  Apply Now
                </button>
              </a>
            </div>

          </div>
        </div>

        {/* CTA Cards */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
          
          {/* Sponsor Card */}
          <a 
            href="https://wa.me/7808056151?text=Hi%20Bhavishya,%20I%20am%20interested%20in%20sponsoring%20a%20child.%20Can%20you%20please%20provide%20more%20details%3F" 
            target="_blank" 
            rel="noopener noreferrer"
            className='block group'
          >
            <div className='bg-[#EE645A] rounded-[16px] overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105 h-full'>
              <img 
                src={sponsorAChildSvg}
                alt="Sponsor a Child"
                className='w-full h-48 object-cover'
              />
              <div className='p-6 text-white text-center'>
                <h3 className='font-bold text-[20px] sm:text-[24px] mb-2'>
                  Sponsor a Child
                </h3>
                <p className='text-[14px] text-[#FFFFFFCC]'>
                  Give a child the gift of education and opportunity
                </p>
              </div>
            </div>
          </a>

          {/* Volunteer Card */}
          <a 
            href="https://wa.me/7808056151?text=Hi%20Bhavishya,%20I%20would%20like%20to%20volunteer%20with%20your%20organization.%20Can%20you%20tell%20me%20about%20upcoming%20opportunities%3F" 
            target="_blank" 
            rel="noopener noreferrer"
            className='block group'
          >
            <div className='bg-[#EE645A] rounded-[16px] overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105 h-full'>
              <img 
                src={volunteerWithUsSvg}
                alt="Volunteer with us"
                className='w-full h-48 object-cover'
              />
              <div className='p-6 text-white text-center'>
                <h3 className='font-bold text-[20px] sm:text-[24px] mb-2'>
                  Volunteer with us
                </h3>
                <p className='text-[14px] text-[#FFFFFFCC]'>
                  Be the change you want to see in the world
                </p>
              </div>
            </div>
          </a>

          {/* Partner Card */}
          <a 
            href="https://wa.me/7808056151?text=Hi%20Bhavishya,%20I%20am%20interested%20in%20partnering%20with%20your%20organization.%20Can%20we%20discuss%20partnership%20opportunities%3F" 
            target="_blank" 
            rel="noopener noreferrer"
            className='block group'
          >
            <div className='bg-[#EE645A] rounded-[16px] overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105 h-full'>
              <img 
                src={partnerWithUsSvg}
                alt="Partner with us"
                className='w-full h-48 object-cover'
              />
              <div className='p-6 text-white text-center'>
                <h3 className='font-bold text-[20px] sm:text-[24px] mb-2'>
                  Partner with us
                </h3>
                <p className='text-[14px] text-[#FFFFFFCC]'>
                  Together we can create lasting impact and change
                </p>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* Footer CTA */}
      <div className='mt-12 bg-[#EE645A] text-white py-12 px-4'>
        <div className='max-w-[1200px] mx-auto text-center'>
          <h2 className='font-bold text-[24px] sm:text-[32px] mb-4'>
            Ready to Make a Difference?
          </h2>
          <p className='text-[16px] text-[#FFFFFFCC] mb-6 max-w-[700px] mx-auto'>
            Contact us today through WhatsApp or fill the form above. We're excited to hear from you!
          </p>
          <a 
            href="https://wa.me/7808056151?text=Hi%20Bhavishya,%20I%20would%20like%20to%20get%20in%20touch%20with%20Bhavishya%20organization.%20Please%20tell%20me%20how%20I%20can%20help%20or%20get%20involved." 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <button className='px-8 py-3 bg-white text-[#EE645A] font-bold text-[16px] rounded-[8px] hover:bg-gray-100 transition-colors duration-300'>
              Chat on WhatsApp
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Contact;